import * as React from 'react';
import { Component } from 'react';
import { observable, computed, action } from 'mobx';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import styled, { withTheme } from 'styled-components';
import { propStyles } from '../utils/propStyles';
import { isDisabled } from '../utils/customPropStyles';
import get from 'lodash/get';
import FundManagerImage from './FundManagerImage';
import FundManagerContent from './FundManagerContent';
import Icon from './Icon';
import { DataStore, AccessibilityStore, EntityStore } from '../stores';
import { FundManager, FundManagerExtended } from '../models/entity';
import { FundManagers as FundManagersConfig } from '../models/configuration/components/FundManagers';
import { ShowExtended } from '../models/configuration/ShowExtended';
import { Link } from '../models/configuration/Link';
import HTag from './HTag';
import { FundManagerImageOptions } from '../models/configuration/FundManagerImageOptions';
import { LinkFilter } from '../models/configuration/LinkFilter';
import { IQueryContext } from '@kurtosys/ksys-api-client/dist/models/commonTypes';
import { sortValues } from '../utils';

const FundManagersWrapper = styled<any, any>('div')`
    ${propStyles};
    position: relative;
`;

FundManagersWrapper.defaultProps = {};

const FundManagersNavArrows = styled<any, any>('li')`
    ${propStyles};
    position: absolute;
    left: 0;
    right: 0;
    z-index: 1;
    display: flex;
    justify-content: space-between;
    opacity: 0;
    transition: opacity 300ms linear;
`;

FundManagersNavArrows.defaultProps = {};

const FundManagersList = styled<any, any>('ol')`
    ${propStyles};
    list-style: none;
    &:hover {
        ${FundManagersNavArrows} {
            opacity: 1;
        }
    }
`;

FundManagersList.defaultProps = {
    position: 'relative',
    display: 'flex',
    alignItems: ['flex-start', 'center'],
    flexWrap: 'nowrap',
    overflowX: 'hidden',
    marginY: ['0', 'md'],
};

const FundManagersListItem = styled<any, any>('li')`
    ${propStyles};
    transition: transform 300ms ease-in-out;
    transform: ${p => `translate3d(${-100 * p.activeSlide}%, 0, 0)`};
`;

FundManagersListItem.defaultProps = {
    display: 'flex',
    flex: '0 0 auto',
    maxWidth: '100%',
    flexDirection: ['row', 'column'],
    alignItems: ['flex-start', 'stretch'],
    bgColor: 'white',
    borderWidth: '1px',
    borderStyle: ['none none solid none', 'solid'],
    borderColor: 'gray300',
    borderRadius: ['0', 'sm'],
    boxShadow: ['none', 'sm'],
    padding: 'md',
};

const FundManagerDetails = styled<any, any>('div')`
    ${propStyles};
`;

FundManagerDetails.defaultProps = {
    margin: ['0 0 0 1em', '0'],
    width: '100%',
};

const FundManagersNav = styled<any, any>('nav')`
    ${propStyles};
`;

FundManagersNav.defaultProps = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginY: 'md',
};

const FundManagersNavButton = styled<any, any>('button')`
    ${propStyles};
    ${isDisabled};
    outline: none;
    cursor: pointer;
    &:disabled {
        opacity: 0.25;
    }
`;

FundManagersNavButton.defaultProps = {
    bgColor: 'transparent',
    borderStyle: 'none',
    fontSize: 'lg',
    padding: '0 0.1em',
    color: 'gray500',
    selected: {
        color: 'body',
    },
};

interface FundManagersProps {
    theme?: any;
    dataStore?: DataStore;
    accessibilityStore?: AccessibilityStore;
    entityStore?: EntityStore;
    showHeading: boolean;
    fundManagers: Array<FundManager>;
    imageSrc?: string;
}

@inject('dataStore', 'accessibilityStore', 'entityStore')
@observer
class FundManagers extends Component<FundManagersProps> {
    autoPlayId: number;
    @observable activeSlide: number = 0;

    constructor(props) {
        super(props);

        if (!this.props.dataStore.isMobile && this.autoPlayInterval > 0) {
            this.autoPlay(this.autoPlayInterval);
        }
    }

    componentWillUnmount() {
        clearInterval(this.autoPlayId);
    }

    @computed
    get fundManagersConfig(): FundManagersConfig {
        return this.props.dataStore.config.rawRowConfig.fundManagers;
    }

    @computed
    get showBiography(): boolean {
        const { showBiography } = this.fundManagersConfig;
        return showBiography;
    }

    @computed
    get showExtended(): Array<ShowExtended> {
        const { showExtended = [] } = this.fundManagersConfig;
        return showExtended;
    }

    @computed
    get queryContext(): IQueryContext {
        const { entityStore } = this.props;
        const { queryContext = {} } = entityStore;
        return queryContext;
    }

    @computed
    get link(): Link {
        const { link } = this.fundManagersConfig;
        return link;
    }

    @computed
    get url(): LinkFilter {
        const { url } = this.fundManagersConfig;
        return url;
    }

    @computed
    get image(): FundManagerImageOptions {
        const { image } = this.fundManagersConfig;
        return image;
    }

    @computed
    get isCarousel(): boolean {
        const { carousel = true } = this.fundManagersConfig;
        return carousel;
    }

    @computed
    get label(): string {
        const { label } = this.fundManagersConfig;
        return label;
    }

    @computed
    get showImage(): boolean {
        const { showImage } = this.fundManagersConfig;
        return showImage;
    }

    @computed
    get autoPlayInterval(): number {
        const { autoPlay = 0 } = this.fundManagersConfig;
        return this.isCarousel ? autoPlay : 0;
    }

    @computed
    get fundManagers() {
        const { sort } = this.fundManagersConfig;

        const { dataStore } = this.props;
        let { fundManagers } = this.props;
        fundManagers = sortValues(fundManagers, sort);

        return fundManagers.map(fm => {
            // We are including the original fund manager object for the queries to work
            const fundManager = {
                code: fm.code,
                name: fm.name,
                context: fm,
            } as any;

            if (this.showBiography) {
                const biographyData =
                    fm.biography.find(
                        bio => bio.culture === dataStore.culture
                    ) ||
                    fm.biography.find(
                        bio => bio.culture === dataStore.config.defaultCulture
                    );

                if (biographyData) {
                    const { biography } = biographyData;
                    if (biography) {
                        fundManager.biography = biography;
                    }
                }
            }

            if (this.showExtended.length > 0) {
                fundManager.extended = this.showExtended.map(extended => ({
                    label: extended.label,
                    value: this.getExtendedValue(
                        extended.property,
                        fm.extended
                    ),
                }));
            }

            if (this.link) {
                fundManager.link = {
                    label: this.link.label,
                    value: this.getExtendedValue(
                        this.link.property,
                        fm.extended
                    ),
                };
            }

            if (this.url) {
                fundManager.url = { ...this.url };
            }

            if (this.image && this.image.property) {
                fundManager.image = this.getExtendedValue(
                    this.image.property,
                    fm.extended
                );
            }

            return fundManager;
        });
    }

    getExtendedValue = (
        property: string,
        extended: FundManagerExtended
    ): string => {
        return get(
            extended,
            `${property}.value`,
            get(extended, `${property}`, '')
        );
    };

    @action
    setActiveSlide = (index: number) => {
        this.activeSlide = index;
        this.autoPlay(this.autoPlayInterval);
    };

    @computed
    get prevSlideDisabled(): boolean {
        return this.activeSlide === 0;
    }

    @computed
    get nextSlideDisabled(): boolean {
        return this.activeSlide === this.fundManagers.length - 1;
    }

    @action
    prevSlide = () => {
        this.activeSlide = this.prevSlideDisabled
            ? this.fundManagers.length - 1
            : this.activeSlide - 1;
        this.autoPlay(this.autoPlayInterval);
    };

    @action
    nextSlide = () => {
        this.activeSlide = this.nextSlideDisabled ? 0 : this.activeSlide + 1;
        this.autoPlay(this.autoPlayInterval);
    };

    @action
    autoPlay = t => {
        if (t > 0) {
            if (this.autoPlayId) {
                clearInterval(this.autoPlayId);
            }
            this.autoPlayId = setInterval(this.nextSlide, t) as any;
        }
    };

    render() {
        const {
            theme,
            dataStore,
            showHeading,
            imageSrc,
            accessibilityStore,
        } = this.props;
        const {
            fundManagers,
            setActiveSlide,
            prevSlide,
            nextSlide,
            activeSlide,
            prevSlideDisabled,
            nextSlideDisabled,
            isCarousel,
        } = this;

        return (
            <FundManagersWrapper {...theme.FundManagersWrapper}>
                {showHeading && (
                    <HTag
                        componentTheme={'FundManagersHeading'}
                        defaultStyleProps={{
                            color: 'body',
                            fontFamily: 'heading',
                            fontWeight: 'bold',
                            fontSize: 'md',
                        }}
                        level={2}
                        text={this.label}
                    />
                )}
                <FundManagersList
                    flexDirection={isCarousel ? ['column', 'row'] : 'column'}
                    {...theme.FundManagersList}
                >
                    {!dataStore.isMobile &&
                        isCarousel &&
                        fundManagers.length > 1 && (
                            <FundManagersNavArrows
                                {...theme.FundManagersNavArrows}
                            >
                                <FundManagersNavButton
                                    aria-label={accessibilityStore.buttonAccessibilityValue(
                                        'FundManagersNavButtonPrevious'
                                    )}
                                    onClick={() => prevSlide()}
                                    disabled={prevSlideDisabled}
                                    padding="sm"
                                    data-metric-id={
                                        'fundManagerNav-button-previous'
                                    }
                                    {...theme.FundManagersNavArrowButton}
                                >
                                    <Icon fileName="FundManagersNavArrowLeftIcon" />
                                </FundManagersNavButton>
                                <FundManagersNavButton
                                    aria-label={accessibilityStore.buttonAccessibilityValue(
                                        'FundManagersNavButtonNext'
                                    )}
                                    onClick={() => nextSlide()}
                                    disabled={nextSlideDisabled}
                                    padding="sm"
                                    data-metric-id={
                                        'fundManagerNav-button-next'
                                    }
                                    {...theme.FundManagersNavArrowButton}
                                >
                                    <Icon fileName="FundManagersNavArrowRightIcon" />
                                </FundManagersNavButton>
                            </FundManagersNavArrows>
                        )}
                    {fundManagers.map((fundManager, index) => (
                        <FundManagersListItem
                            key={fundManager.code}
                            data-selected={activeSlide === index}
                            activeSlide={activeSlide}
                            {...theme.FundManagersListItem}
                        >
                            {this.showImage && (
                                <FundManagerImage
                                    name={fundManager.name}
                                    code={fundManager.code}
                                    src={imageSrc || fundManager.image}
                                />
                            )}
                            {dataStore.isMobile ? (
                                <FundManagerDetails
                                    {...theme.FundManagerDetailsMobile}
                                >
                                    <FundManagerContent
                                        fundManager={fundManager}
                                    />
                                </FundManagerDetails>
                            ) : (
                                <FundManagerContent fundManager={fundManager} />
                            )}
                        </FundManagersListItem>
                    ))}
                </FundManagersList>
                {!dataStore.isMobile && isCarousel && fundManagers.length > 1 && (
                    <FundManagersNav {...theme.FundManagersNav}>
                        {fundManagers.map((fm, index) => (
                            <FundManagersNavButton
                                aria-label={accessibilityStore.buttonAccessibilityValue(
                                    'FundManagersNavButton'
                                )}
                                key={fm.code}
                                onClick={() => setActiveSlide(index)}
                                data-selected={activeSlide === index}
                                data-metric-id={`fundManagerNav-button-${index}`}
                                {...theme.FundManagersNavButton}
                            >
                                {'●'}
                            </FundManagersNavButton>
                        ))}
                    </FundManagersNav>
                )}
            </FundManagersWrapper>
        );
    }
}

export default withTheme(FundManagers) as any;



// WEBPACK FOOTER //
// ./src/Components/FundManagers.tsx